import { useContext, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Form } from '@zillow/constellation-10';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { DISPLAY_SERVER_ERROR_AT, EXIT_CTA_LABEL } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import { useSelector } from '../../../selectors/useSelector';

import WizardAlert from '../../WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import PageWizard from '../../Wizard/PageWizard';

import { RECS } from '../../../constants/RecommendationsConstants';

import FormSectionContainer from './components/FormSectionContainer';
import WizardTipContent from './components/WizardTipContent';
import WizardTipContentRoomForRent from './components/WizardTipContentRoomForRent';

import { WizardFlexContainer } from '../../../styles/wizard';

import type { FormEvent, ReactNode } from 'react';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const PropertyDescription = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const { recommendations, currentProperty } = useSelector(
    (state) => ({
      recommendations: state.properties.recommendations.recommendations,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const squareFootageRec = recommendations?.find((item: { type: string }) => item.type === RECS.SQUARE_FOOTAGE);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideBackButton={!isRoomForRent}
      {...rest}
    >
      <div data-testid="PropertyDescription">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <WizardFlexContainer>
          {/* cast as div to prevent cannot appear as a descendant of form error */}
          <Form asChild={true} onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}>
            <div>
              <FormSectionContainer />
            </div>
          </Form>
          {isRoomForRent === false && !!squareFootageRec?.outcomeStats && (
            <WizardTipCardContainer
              header="Why show square footage?"
              showDesktopViewBreakpoint={showDesktopViewBreakpoint}
            >
              <WizardTipContent outcomeStats={squareFootageRec?.outcomeStats} source={squareFootageRec?.source?.tip} />
            </WizardTipCardContainer>
          )}

          {isRoomForRent && (
            <WizardTipCardContainer
              header="Including square footage"
              showDesktopViewBreakpoint={showDesktopViewBreakpoint}
            >
              <WizardTipContentRoomForRent />
            </WizardTipCardContainer>
          )}
        </WizardFlexContainer>
      </div>
    </PageWizard>
  );
};

export default PropertyDescription;
